'use client';

import { type CSSProperties, forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { PropertyCardRootContext } from './context/property-card-root.context';
import { PropertyCardPhotoFallback } from './property-card-photo-fallback';
import { propertyCardPhotoRecipe } from './styles/property-card-photo.styles';
import type { PropertyCardPhotoPropsInterface } from './types/property-card-photo.types';

/**
 * Used to display a single property photo in a `PropertyCard`.
 *
 * @since 8.63.0
 */
export const PropertyCardPhoto = forwardRef<HTMLDivElement, PropertyCardPhotoPropsInterface>(
  (props: PropertyCardPhotoPropsInterface, forwardedRef) => {
    const { appearance = 'large' } = useContext(PropertyCardRootContext);
    const {
      'aria-label': ariaLabel,
      alt = '',
      asChild = false,
      className,
      css: cssProp,
      draggable = true,
      fallbackImg = <PropertyCardPhotoFallback />,
      photoOrientation = 'landscape',
      sizes,
      src,
      srcSet,
      children,
      ...rest
    } = props;

    const Component = asChild ? Slot : 'img';

    // Use <img> element instead of <Image> component to avoid `overflow: visible`
    // warning related to using `object-fit: cover`
    const ImageElement =
      src || asChild ? (
        <Component
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          aria-hidden="false"
          aria-label={ariaLabel}
          draggable={draggable}
          className="c11n"
        >
          {children}
        </Component>
      ) : (
        fallbackImg
      );

    const styles = css(propertyCardPhotoRecipe.raw({ appearance, photoOrientation }), cssProp);

    return (
      <div
        ref={forwardedRef}
        data-c11n-component="PropertyCard.Photo"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_property-card__photo', styles, className)}
        style={
          {
            '--c11n-background-image': `url(${src})`,
          } as CSSProperties
        }
        {...rest}
      >
        {ImageElement}
      </div>
    );
  },
);

PropertyCardPhoto.displayName = 'PropertyCard.Photo';
